/**
 * Утилиты для работы с путями к документам
 *
 * Этот файл содержит вспомогательные функции для работы с документами,
 * которые обеспечивают безопасное и удобное использование путей к документам.
 */

import { DOCUMENTS, DocumentCategory } from '@/config/documents';

/**
 * Проверяет, существует ли документ по указанному пути
 * @param path Путь к документу
 * @returns Promise, который разрешается в true, если документ существует
 */
export async function documentExists(path: string): Promise<boolean> {
  if (!path) return false;
  
  try {
    // В режиме разработки используем fetch для проверки
    if ((import.meta as any).env?.DEV) {
      const response = await fetch(path, { method: 'HEAD' });
      return response.ok;
    }
    
    // В продакшене предполагаем, что документ существует,
    // если путь указан корректно
    return path.startsWith('/documents/') && path.length > 0;
  } catch (error) {
    console.warn(`Не удалось проверить существование документа: ${path}`, error);
    return false;
  }
}

/**
 * Получает имя файла из пути к документу
 * @param path Путь к документу
 * @returns Имя файла с расширением
 */
export function getDocumentFilename(path: string): string {
  if (!path) return '';
  
  const parts = path.split('/');
  return parts[parts.length - 1] || '';
}

/**
 * Получает расширение файла из пути к документу
 * @param path Путь к документу
 * @returns Расширение файла (например, "pdf", "docx")
 */
export function getDocumentExtension(path: string): string {
  const filename = getDocumentFilename(path);
  const lastDotIndex = filename.lastIndexOf('.');
  
  if (lastDotIndex === -1) return '';
  return filename.slice(lastDotIndex + 1).toLowerCase();
}

/**
 * Получает тип документа на основе расширения файла
 * @param path Путь к документу
 * @returns Тип документа (PDF, Word, Excel и т.д.)
 */
export function getDocumentType(path: string): string {
  const extension = getDocumentExtension(path);
  
  const typeMap: Record<string, string> = {
    'pdf': 'PDF',
    'doc': 'Word',
    'docx': 'Word',
    'xls': 'Excel',
    'xlsx': 'Excel',
    'ppt': 'PowerPoint',
    'pptx': 'PowerPoint',
    'jpg': 'Изображение',
    'jpeg': 'Изображение',
    'png': 'Изображение',
    'gif': 'Изображение',
    'txt': 'Текст',
    'zip': 'Архив',
    'rar': 'Архив',
  };
  
  return typeMap[extension] || extension.toUpperCase();
}

/**
 * Получает размер файла в читаемом формате
 * @param bytes Размер файла в байтах
 * @returns Строка с размером файла (например, "2.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Создает безопасную ссылку для документа
 * @param path Путь к документу
 * @param filename Опциональное имя файла для скачивания
 * @returns Объект с атрибутами для тега <a>
 */
export function createDocumentLink(path: string, filename?: string) {
  const downloadFilename = filename || getDocumentFilename(path);
  const isPdf = getDocumentExtension(path) === 'pdf';
  
  // Для PDF файлов открываем в браузере, для остальных - скачиваем
  const linkAttributes: {
    href: string;
    download?: string;
    target?: string;
    rel?: string;
  } = {
    href: path,
  };
  
  if (isPdf) {
    // PDF открываем в новой вкладке
    linkAttributes.target = '_blank';
    linkAttributes.rel = 'noopener noreferrer';
  } else {
    // Остальные файлы скачиваем
    linkAttributes.download = downloadFilename;
  }
  
  return linkAttributes;
}

/**
 * Создает безопасную ссылку для скачивания документа (устаревшая, используйте createDocumentLink)
 * @deprecated Используйте createDocumentLink вместо этой функции
 */
export function createDownloadLink(path: string, filename?: string) {
  console.warn('Функция createDownloadLink устарела. Используйте createDocumentLink.');
  return createDocumentLink(path, filename);
}

/**
 * Получает все документы из указанной категории в формате для отображения
 * @param category Категория документов
 * @returns Массив объектов с информацией о документах
 */
export function getDocumentsForDisplay(category: DocumentCategory) {
  const documents = DOCUMENTS[category];
  
  return Object.entries(documents).map(([key, path]) => ({
    key,
    path,
    filename: getDocumentFilename(path),
    extension: getDocumentExtension(path),
    type: getDocumentType(path),
    // TODO: В будущем можно добавить метаданные (размер, дата изменения и т.д.)
  }));
}

/**
 * Получает документы по годам для отображения на странице участников
 * @returns Массив объектов с информацией о сборниках заданий по годам
 */
export function getTaskCollectionsByYear() {
  const taskCollections = DOCUMENTS.TASK_COLLECTIONS;
  
  return Object.entries(taskCollections)
    .filter(([_key, path]) => path && !path.endsWith('/')) // Исключаем директории
    .map(([key, path]) => {
      const year = key.replace('YEAR_', '');
      const filename = getDocumentFilename(path);
      
      return {
        year,
        path,
        filename,
        title: `Сборник заданий ${year} года`,
        description: `Задания отборочного этапа ${year} года в формате PDF`,
        icon: 'Download' as const,
      };
    })
    .sort((a, b) => b.year.localeCompare(a.year)); // Сортировка по убыванию года
}

/**
 * Валидирует путь к документу
 * @param path Путь к документу
 * @returns Объект с результатом валидации
 */
export function validateDocumentPath(path: string): {
  isValid: boolean;
  error?: string;
} {
  if (!path) {
    return { isValid: false, error: 'Путь к документу не указан' };
  }
  
  if (!path.startsWith('/')) {
    return { isValid: false, error: 'Путь должен начинаться с /' };
  }
  
  if (!path.includes('.')) {
    return { isValid: false, error: 'Путь должен содержать расширение файла' };
  }
  
  const extension = getDocumentExtension(path);
  const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'jpg', 'jpeg', 'png'];
  
  if (!allowedExtensions.includes(extension)) {
    return { 
      isValid: false, 
      error: `Расширение .${extension} не поддерживается. Разрешенные расширения: ${allowedExtensions.join(', ')}` 
    };
  }
  
  return { isValid: true };
}